// frontend/src/components/cart/CartSidebar.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { FiX, FiPlus, FiMinus, FiTrash2, FiShoppingBag } from 'react-icons/fi';
import { useCart } from '../../context/CartContext';
import { formatPrice } from '../../utils/api';

export default function CartSidebar({ open, isOpen, onClose }) {
  const visible = open ?? isOpen ?? false;

  const items = useCart((state) => state.items);
  const removeItem = useCart((state) => state.removeItem);
  const updateQuantity = useCart((state) => state.updateQuantity);

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const deliveryFee = subtotal >= 100000 ? 0 : 2000;
  const total = subtotal + deliveryFee;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity ${
          visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-[#111111] border-l border-gray-200 dark:border-[#1f1f1f] z-50 transform transition-transform duration-300 flex flex-col ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-[#1f1f1f]">
          <div className="flex items-center gap-2">
            <FiShoppingBag className="text-primary-600 dark:text-primary-500" />

            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Mon Panier
              <span className="ml-2 text-sm text-primary-600 dark:text-primary-500">
                ({itemCount})
              </span>
            </h2>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <FiShoppingBag className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />

              <p className="text-gray-500 dark:text-gray-400">
                Votre panier est vide
              </p>

              <Link
                to="/produits"
                onClick={onClose}
                className="inline-block mt-4 text-primary-600 dark:text-primary-500 hover:underline"
              >
                Voir les produits
              </Link>
            </div>
          ) : (
            items.map((item, index) => (
              <div
                key={item.key || index}
                className="flex gap-3 bg-gray-50 dark:bg-[#0a0a0a] p-3 rounded-xl border border-gray-200 dark:border-[#1f1f1f]"
              >
                {/* Image */}
                <img
                  src={item.image || `https://picsum.photos/seed/${item.id}/80/80`}
                  alt={item.name}
                  className="w-20 h-20 rounded-lg object-cover flex-shrink-0 bg-gray-100 dark:bg-[#111111]"
                  onError={(e) => {
                    e.target.src = `https://picsum.photos/seed/${item.id}/80/80`;
                  }}
                />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 dark:text-white text-sm font-medium line-clamp-2">
                    {item.name}
                  </p>

                  {(item.color || item.size) && (
                    <div className="flex flex-wrap gap-2 mt-1 text-xs">
                      {item.color && (
                        <span className="text-gray-500 dark:text-gray-400">
                          <span className="text-gray-400 dark:text-gray-600">Couleur:</span> {item.color}
                        </span>
                      )}

                      {item.size && (
                        <span className="text-gray-500 dark:text-gray-400">
                          <span className="text-gray-400 dark:text-gray-600">Taille:</span> {item.size.toUpperCase()}
                        </span>
                      )}
                    </div>
                  )}

                  <p className="text-primary-600 dark:text-primary-400 text-sm font-medium mt-1">
                    {formatPrice(item.price)}
                  </p>

                  {/* Quantity controls */}
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => updateQuantity(item.key, item.quantity - 1)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-200 dark:bg-[#1f1f1f] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                      <FiMinus className="w-3 h-3" />
                    </button>

                    <span className="text-gray-900 dark:text-white text-sm font-medium w-6 text-center">
                      {item.quantity}
                    </span>

                    <button
                      onClick={() => updateQuantity(item.key, item.quantity + 1)}
                      disabled={item.quantity >= (item.stock || 99)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-200 dark:bg-[#1f1f1f] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50"
                    >
                      <FiPlus className="w-3 h-3" />
                    </button>

                    <button
                      onClick={() => removeItem(item.key)}
                      className="ml-auto p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-gray-200 dark:border-[#1f1f1f] p-4 space-y-3 bg-white dark:bg-[#111111]">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Sous-total</span>
                <span className="text-gray-900 dark:text-white">{formatPrice(subtotal)}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Livraison</span>

                <span className={deliveryFee === 0 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-white'}>
                  {deliveryFee === 0 ? 'Gratuite 🎉' : formatPrice(deliveryFee)}
                </span>
              </div>

              <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-200 dark:border-[#1f1f1f]">
                <span className="text-gray-900 dark:text-white">Total</span>
                <span className="text-primary-600 dark:text-primary-400">{formatPrice(total)}</span>
              </div>
            </div>

            <Link
              to="/checkout"
              onClick={onClose}
              className="block w-full py-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-center transition-colors"
            >
              Commander maintenant
            </Link>
          </div>
        )}
      </div>
    </>
  );
}